'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { FeatherFileText } from '@subframe/core';
import { EmptyState, LazyLoader } from '@/shared/components/ui';
import { estadoPosSentencaDoProcesso, FaixaEstadoPosSentenca, podeAfirmarEstadoPosSentenca, sentencaFoiProferida } from '@/features/encerramento-processo';
import { BaixarProcessoAcaoContainer } from '@/features/baixar-processo';
import { selectCurrentEscritorio } from '@/features/escritorio/store/crud/selectors';
import { useArbitralDetailPage } from '@/features/arbitral-detalhes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { criarSolicitacaoArbitral, selectSolicitacoesAbertas, selectSubmittingSolicitacao } from '../store';
import type { CriarSolicitacaoPayload } from '../types/arbitralDetailTypes';
import { escopoDeSolicitacaoDaEmpresa } from '../types/capacidadesProcessoArbitral';
import { useAutorizacaoAr } from '../hooks/useAutorizacaoAr';
import { useCustasProcesso } from '../hooks/useCustasProcesso';
import { useEnvioFisicoAr } from '../hooks/useEnvioFisicoAr';
import { useFinanceiroProcesso } from '../hooks/useFinanceiroProcesso';
import { useJuntarDocumentoPadrao } from '../hooks/useJuntarDocumentoPadrao';
import { pendenciaAguardaRespostaDaParte, pendenciaEncerrada } from '../utils/arbitralDetailUtils';
import { montarCobrancasCliente } from '../utils/financeiroProcessoClienteUtils';
import { ArbitralDetailHeader } from './ArbitralDetailHeader';
import { AutorizacaoArBanner } from './AutorizacaoArBanner';
import { AutorizacaoArBloco } from './tabs/resumo/AutorizacaoArBloco';
import { CustasPendentesBanner } from './CustasPendentesBanner';
import { FaixaAguardandoTriagem } from './FaixaAguardandoTriagem';
import { CancelProcessModal } from './modals/CancelProcessModal';
import { ReabrirProcessoModal } from './modals/ReabrirProcessoModal';
import { ArbitralDetailTabs } from './tabs/ArbitralDetailTabs';
import { useOrigemAbaAuditoria } from '@/shared/hooks/useOrigemAbaAuditoria';
const ArbitralResumoTab = dynamic(() => import('./tabs/resumo/ArbitralResumoTab').then(m => m.ArbitralResumoTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralPendenciasTab = dynamic(() => import('./tabs/pendencias/ArbitralPendenciasTab').then(m => m.ArbitralPendenciasTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralDocumentosTab = dynamic(() => import('./tabs/documentos/ArbitralDocumentosTab').then(m => m.ArbitralDocumentosTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralAndamentosTab = dynamic(() => import('./tabs/andamentos/ArbitralAndamentosTab').then(m => m.ArbitralAndamentosTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralChatTab = dynamic(() => import('./tabs/chat/ArbitralChatTab').then(m => m.ArbitralChatTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralCertificadosTab = dynamic(() => import('./tabs/certificados/ArbitralCertificadosTab').then(m => m.ArbitralCertificadosTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const EnvioFisicoArTab = dynamic(() => import('./tabs/envioFisico/EnvioFisicoArTab').then(m => m.EnvioFisicoArTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
const ArbitralFinanceiroTab = dynamic(() => import('./tabs/financeiro/ArbitralFinanceiroTab').then(m => m.ArbitralFinanceiroTab), {
  loading: () => <LazyLoader />,
  ssr: false
});
interface ArbitralDetailPageContainerProps {
  processoId: string;
}
function ArbitralDetailPageContainer(t0) {
  const $ = _c(224);
  const {
    processoId
  } = t0;
  useOrigemAbaAuditoria("PAINEL_ESCRITORIO");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isReabrirModalOpen, setIsReabrirModalOpen] = useState(false);
  const [dadosDrawerAberto, setDadosDrawerAberto] = useState(false);
  const {
    envios: enviosAr,
    acaoEmVoo: acaoArEmVoo,
    popupBloqueado: popupArBloqueado,
    autorizarConvenio,
    pagarEnvio,
    confirmarPagamento,
    voltarParaCobranca,
    devolverEndereco,
    recusarEnvio,
    desfazerRecusa
  } = useAutorizacaoAr(processoId);
  const [highlightAndamentoId, setHighlightAndamentoId] = useState(null);
  const [abrirComposerAndamento, setAbrirComposerAndamento] = useState(false);
  const solicitacoesAbertas = useAppSelector(selectSolicitacoesAbertas);
  const submittingSolicitacao = useAppSelector(selectSubmittingSolicitacao);
  const isGuestWorkspace = !!useAppSelector(selectCurrentEscritorio)?.guestAccess;
  const usarDocumentoPadrao = useJuntarDocumentoPadrao(processoId);
  const dispatch = useAppDispatch();
  let t1;
  if ($[0] !== processoId) {
    t1 = {
      processoId
    };
    $[0] = processoId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    activeTab,
    setActiveTab,
    isLoading,
    error,
    processo,
    documentos,
    loadingDocumentos,
    mensagensChat,
    isClosed,
    chatMessage,
    setChatMessage,
    isSendingMessage,
    handleSendMessage,
    handleBackToList,
    handleExportHistory,
    exportandoHistorico,
    handleCancelProcess,
    isCanceling,
    handleReabrirProcesso,
    reabrindo,
    handlePatchParte,
    handleReenviarNotificacao,
    patchingParteId,
    reenvioParteId,
    certificados,
    certificadosLoading,
    handleVerCertificadoAcessos,
    handleVerCertificadoAcessosSentenca,
    handleBaixarCertificado,
    submittingPendenciaId,
    isParteAtiva,
    handleResolverPendencia,
    handleReenviarPendencia,
    removingDocumentoId,
    currentUserId,
    currentUserName,
    handleRemoverDocumento,
    handleDownloadDocumento,
    handleGetPreviewUrl,
    loadingAndamentos,
    errorAndamentos,
    handleRecarregarAndamentos,
    submittingAndamento,
    handleCriarAndamento,
    typingUsers
  } = useArbitralDetailPage(t1);
  const {
    financeiro: rawFinanceiro,
    cobrancas: cobrancasApi,
    extrato: extratoApi,
    carregando: carregandoFinanceiro,
    erro: erroFinanceiro,
    recarregar: recarregarFinanceiro
  } = useFinanceiroProcesso(processoId);
  let t2;
  if ($[2] !== erroFinanceiro || $[3] !== processoId || $[4] !== recarregarFinanceiro) {
    t2 = {
      processoId,
      onRecarregar: recarregarFinanceiro,
      erroLeitura: erroFinanceiro
    };
    $[2] = erroFinanceiro;
    $[3] = processoId;
    $[4] = recarregarFinanceiro;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    custas,
    popupBloqueado: popupCustasBloqueado,
    pagarCustas,
    confirmarPagamento: confirmarPagamentoCustas,
    voltarParaCobranca: voltarParaCobrancaCustas,
    regerarCobranca,
    regerando: regerandoCustas,
    pagamentoSuspenso: pagamentoCustasSuspenso,
    regerandoId: regerandoCobrancaId,
    linkSuspenso,
    regerarPorId
  } = useCustasProcesso(rawFinanceiro, cobrancasApi, t2);
  let t3;
  if ($[6] !== activeTab || $[7] !== processoId || $[8] !== setActiveTab) {
    t3 = {
      processoArbitralId: processoId,
      abaAtiva: activeTab,
      irParaAba: setActiveTab
    };
    $[6] = activeTab;
    $[7] = processoId;
    $[8] = setActiveTab;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const envioFisicoAr = useEnvioFisicoAr(t3);
  let t4;
  if ($[10] !== dispatch || $[11] !== processoId) {
    t4 = async payload => {
      const result = await dispatch(criarSolicitacaoArbitral({
        processoId,
        payload
      }));
      if (criarSolicitacaoArbitral.fulfilled.match(result)) {
        return null;
      }
      return result.payload as string | undefined ?? "Tente novamente em instantes.";
    };
    $[10] = dispatch;
    $[11] = processoId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleCriarSolicitacao = t4;
  if (error && !processo) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex h-full w-full flex-col items-center justify-center p-9"><EmptyState icon={<FeatherFileText />} title={"Processo n\xE3o encontrado"} description={"Este processo n\xE3o existe ou voc\xEA n\xE3o tem acesso a ele."} /></div>;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (isLoading || !processo) {
    let t5;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <LazyLoader />;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  let t5;
  if ($[15] !== setActiveTab) {
    t5 = () => {
      setActiveTab("andamentos");
      setAbrirComposerAndamento(true);
    };
    $[15] = setActiveTab;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleAddAndamento = t5;
  let t6;
  if ($[17] !== setActiveTab) {
    t6 = () => {
      setActiveTab("resumo");
      setDadosDrawerAberto(true);
    };
    $[17] = setActiveTab;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleEditarDados = t6;
  let t7;
  if ($[19] !== setActiveTab) {
    t7 = andamentoId => {
      setHighlightAndamentoId(andamentoId);
      setActiveTab("andamentos");
    };
    $[19] = setActiveTab;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleVerAndamento = t7;
  let t8;
  if ($[21] !== solicitacoesAbertas) {
    t8 = solicitacoesAbertas.some(_temp);
    $[21] = solicitacoesAbertas;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const cancelamentoSolicitado = t8;
  let t9;
  if ($[23] !== solicitacoesAbertas) {
    t9 = solicitacoesAbertas.some(_temp2);
    $[23] = solicitacoesAbertas;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const dadosSolicitados = t9;
  const preTriagem = processo.status === "PROCESSO_DISTRIBUIDO" || processo.status === "TRIAGEM_EM_ANDAMENTO";
  const podeCancelarDireto = preTriagem;
  let pendenciasAtivasCliente;
  let pendenciasDoCliente;
  let t10;
  if ($[25] !== preTriagem || $[26] !== processo.pendencias) {
    pendenciasDoCliente = processo.pendencias.filter(_temp3);
    pendenciasAtivasCliente = pendenciasDoCliente.filter(_temp4).length;
    t10 = preTriagem && !pendenciasDoCliente.some(pendenciaAguardaRespostaDaParte);
    $[25] = preTriagem;
    $[26] = processo.pendencias;
    $[27] = pendenciasAtivasCliente;
    $[28] = pendenciasDoCliente;
    $[29] = t10;
  } else {
    pendenciasAtivasCliente = $[27];
    pendenciasDoCliente = $[28];
    t10 = $[29];
  }
  const mostrarFaixaAguardandoTriagem = t10;
  let t11;
  if ($[30] !== cobrancasApi || $[31] !== processo.tipoRito) {
    t11 = montarCobrancasCliente(cobrancasApi, processo.tipoRito);
    $[30] = cobrancasApi;
    $[31] = processo.tipoRito;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const cobrancas = t11;
  const resumoAvulso = rawFinanceiro?.modalidade === "AVULSO" ? rawFinanceiro.resumo : null;
  const cobrancasEmAberto = resumoAvulso?.quantidadeEmAberto ?? 0;
  const cobrancaVencida = resumoAvulso?.temVencida ?? false;
  const arMaisUrgente = enviosAr[0] ?? null;
  const tabEmExibicao = envioFisicoAr.abaEmExibicao;
  const t12 = activeTab === "andamentos";
  let t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsCancelModalOpen(true);
    t14 = () => setIsReabrirModalOpen(true);
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  let t15;
  if ($[35] !== processo.codigo || $[36] !== processoId) {
    t15 = <BaixarProcessoAcaoContainer numeroProcesso={processo.codigo} processoId={processoId} />;
    $[35] = processo.codigo;
    $[36] = processoId;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== cancelamentoSolicitado || $[39] !== dadosSolicitados || $[40] !== exportandoHistorico || $[41] !== handleAddAndamento || $[42] !== handleBackToList || $[43] !== handleEditarDados || $[44] !== handleExportHistory || $[45] !== pendenciasAtivasCliente || $[46] !== podeCancelarDireto || $[47] !== processo || $[48] !== t12 || $[49] !== t15) {
    t16 = <ArbitralDetailHeader processo={processo} pendenciasAtivasCount={pendenciasAtivasCliente} onBackToList={handleBackToList} onAddAndamento={handleAddAndamento} onExportHistory={handleExportHistory} exportandoHistorico={exportandoHistorico} esconderRegistrarAndamento={t12} onEditarDados={handleEditarDados} onCancelProcess={t13} onReabrir={t14} podeCancelarDireto={podeCancelarDireto} cancelamentoSolicitado={cancelamentoSolicitado} dadosSolicitados={dadosSolicitados} podeCancelarProcesso={true} podeEditarIdentificacao={true} acaoBaixarProcesso={t15} />;
    $[38] = cancelamentoSolicitado;
    $[39] = dadosSolicitados;
    $[40] = exportandoHistorico;
    $[41] = handleAddAndamento;
    $[42] = handleBackToList;
    $[43] = handleEditarDados;
    $[44] = handleExportHistory;
    $[45] = pendenciasAtivasCliente;
    $[46] = podeCancelarDireto;
    $[47] = processo;
    $[48] = t12;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== custas || $[52] !== isClosed || $[53] !== pagamentoCustasSuspenso || $[54] !== pagarCustas || $[55] !== regerandoCustas || $[56] !== regerarCobranca) {
    t17 = custas && !isClosed && <CustasPendentesBanner custas={custas} onPagar={pagarCustas} regerando={regerandoCustas} pagamentoSuspenso={pagamentoCustasSuspenso} onRegerarCobranca={regerarCobranca} />;
    $[51] = custas;
    $[52] = isClosed;
    $[53] = pagamentoCustasSuspenso;
    $[54] = pagarCustas;
    $[55] = regerandoCustas;
    $[56] = regerarCobranca;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== acaoArEmVoo || $[59] !== arMaisUrgente || $[60] !== autorizarConvenio || $[61] !== pagarEnvio) {
    t18 = arMaisUrgente && <AutorizacaoArBanner envio={arMaisUrgente.envio} vencido={arMaisUrgente.vencida} acaoEmVoo={Boolean(acaoArEmVoo[arMaisUrgente.envio.solicitacaoId])} onResolver={() => arMaisUrgente.envio.cobranca === "CONVENIO" ? autorizarConvenio(arMaisUrgente.envio.solicitacaoId) : pagarEnvio(arMaisUrgente.envio.solicitacaoId)} />;
    $[58] = acaoArEmVoo;
    $[59] = arMaisUrgente;
    $[60] = autorizarConvenio;
    $[61] = pagarEnvio;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== mostrarFaixaAguardandoTriagem) {
    t19 = mostrarFaixaAguardandoTriagem ? <FaixaAguardandoTriagem /> : null;
    $[63] = mostrarFaixaAguardandoTriagem;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== processo.status) {
    t20 = podeAfirmarEstadoPosSentenca(processo.status, null) ? <FaixaEstadoPosSentenca estado={estadoPosSentencaDoProcesso(processo.status, null)} prazo={null} intimadasEm={null} /> : null;
    $[65] = processo.status;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== certificados.length || $[68] !== cobrancaVencida || $[69] !== cobrancasEmAberto || $[70] !== envioFisicoAr.mostrarAba || $[71] !== pendenciasAtivasCliente || $[72] !== processo.totalMensagensNaoLidas || $[73] !== setActiveTab || $[74] !== tabEmExibicao) {
    t21 = <ArbitralDetailTabs activeTab={tabEmExibicao} onTabChange={setActiveTab} pendenciasAtivasCount={pendenciasAtivasCliente} totalMensagensNaoLidas={processo.totalMensagensNaoLidas} totalCertificados={certificados.length} cobrancasEmAbertoCount={cobrancasEmAberto} temCobrancaVencida={cobrancaVencida} mostrarEnvioFisicoAr={envioFisicoAr.mostrarAba} />;
    $[67] = certificados.length;
    $[68] = cobrancaVencida;
    $[69] = cobrancasEmAberto;
    $[70] = envioFisicoAr.mostrarAba;
    $[71] = pendenciasAtivasCliente;
    $[72] = processo.totalMensagensNaoLidas;
    $[73] = setActiveTab;
    $[74] = tabEmExibicao;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== acaoArEmVoo || $[77] !== autorizarConvenio || $[78] !== confirmarPagamento || $[79] !== confirmarPagamentoCustas || $[80] !== custas || $[81] !== dadosDrawerAberto || $[82] !== desfazerRecusa || $[83] !== devolverEndereco || $[84] !== enviosAr || $[85] !== handleCriarSolicitacao || $[86] !== handlePatchParte || $[87] !== handleReenviarNotificacao || $[88] !== isClosed || $[89] !== pagamentoCustasSuspenso || $[90] !== pagarCustas || $[91] !== pagarEnvio || $[92] !== patchingParteId || $[93] !== popupArBloqueado || $[94] !== popupCustasBloqueado || $[95] !== processo || $[96] !== recusarEnvio || $[97] !== reenvioParteId || $[98] !== regerandoCustas || $[99] !== regerarCobranca || $[100] !== setActiveTab || $[101] !== solicitacoesAbertas || $[102] !== submittingSolicitacao || $[103] !== tabEmExibicao || $[104] !== voltarParaCobranca || $[105] !== voltarParaCobrancaCustas) {
    t22 = tabEmExibicao === "resumo" && <ArbitralResumoTab processo={processo} custasProcesso={{
      custas,
      popupBloqueado: popupCustasBloqueado,
      onPagar: pagarCustas,
      onConfirmarPagamento: confirmarPagamentoCustas,
      onVoltar: voltarParaCobrancaCustas,
      regerando: regerandoCustas,
      pagamentoSuspenso: pagamentoCustasSuspenso,
      onRegerarCobranca: regerarCobranca,
      onVerComprovante: () => setActiveTab("certificados")
    }} blocoAutorizacaoAr={enviosAr.length > 0 ? <>{enviosAr.map(t23 => {
        const {
          envio,
          vencida
        } = t23;
        return <AutorizacaoArBloco key={envio.solicitacaoId} envio={envio} vencida={vencida} popupBloqueado={popupArBloqueado} acaoEmVoo={Boolean(acaoArEmVoo[envio.solicitacaoId])} onAutorizarConvenio={() => autorizarConvenio(envio.solicitacaoId)} onPagar={() => pagarEnvio(envio.solicitacaoId)} onConfirmarPagamento={() => confirmarPagamento(envio.solicitacaoId)} onVoltar={voltarParaCobranca} onDevolverEndereco={observacao => devolverEndereco(envio.solicitacaoId, observacao)} onRecusar={motivo => recusarEnvio(envio.solicitacaoId, motivo)} onDesfazerRecusa={() => desfazerRecusa(envio.solicitacaoId)} />;
      })}</> : null} isClosed={isClosed} escopoDeSolicitacao={escopoDeSolicitacaoDaEmpresa(processo.partes)} solicitacoesAbertas={solicitacoesAbertas} submittingSolicitacao={submittingSolicitacao} onCriarSolicitacao={handleCriarSolicitacao} onPatchParte={handlePatchParte} onReenviarNotificacao={handleReenviarNotificacao} patchingParteId={patchingParteId} reenvioParteId={reenvioParteId} dadosDrawerAberto={dadosDrawerAberto} onDadosDrawerChange={setDadosDrawerAberto} />;
    $[76] = acaoArEmVoo;
    $[77] = autorizarConvenio;
    $[78] = confirmarPagamento;
    $[79] = confirmarPagamentoCustas;
    $[80] = custas;
    $[81] = dadosDrawerAberto;
    $[82] = desfazerRecusa;
    $[83] = devolverEndereco;
    $[84] = enviosAr;
    $[85] = handleCriarSolicitacao;
    $[86] = handlePatchParte;
    $[87] = handleReenviarNotificacao;
    $[88] = isClosed;
    $[89] = pagamentoCustasSuspenso;
    $[90] = pagarCustas;
    $[91] = pagarEnvio;
    $[92] = patchingParteId;
    $[93] = popupArBloqueado;
    $[94] = popupCustasBloqueado;
    $[95] = processo;
    $[96] = recusarEnvio;
    $[97] = reenvioParteId;
    $[98] = regerandoCustas;
    $[99] = regerarCobranca;
    $[100] = setActiveTab;
    $[101] = solicitacoesAbertas;
    $[102] = submittingSolicitacao;
    $[103] = tabEmExibicao;
    $[104] = voltarParaCobranca;
    $[105] = voltarParaCobrancaCustas;
    $[106] = t22;
  } else {
    t22 = $[106];
  }
  let t23;
  if ($[107] !== custas || $[108] !== handleReenviarPendencia || $[109] !== handleResolverPendencia || $[110] !== handleVerAndamento || $[111] !== isClosed || $[112] !== pagamentoCustasSuspenso || $[113] !== pagarCustas || $[114] !== pendenciasDoCliente || $[115] !== submittingPendenciaId || $[116] !== tabEmExibicao) {
    t23 = tabEmExibicao === "pendencias" && <ArbitralPendenciasTab pendencias={pendenciasDoCliente} custas={custas} onPagarCustas={pagarCustas} pagamentoCustasSuspenso={pagamentoCustasSuspenso} isClosed={isClosed} submittingPendenciaId={submittingPendenciaId} onResolver={handleResolverPendencia} onReenviar={handleReenviarPendencia} onVerAndamento={handleVerAndamento} />;
    $[107] = custas;
    $[108] = handleReenviarPendencia;
    $[109] = handleResolverPendencia;
    $[110] = handleVerAndamento;
    $[111] = isClosed;
    $[112] = pagamentoCustasSuspenso;
    $[113] = pagarCustas;
    $[114] = pendenciasDoCliente;
    $[115] = submittingPendenciaId;
    $[116] = tabEmExibicao;
    $[117] = t23;
  } else {
    t23 = $[117];
  }
  let t24;
  if ($[118] !== currentUserId || $[119] !== documentos || $[120] !== handleDownloadDocumento || $[121] !== handleGetPreviewUrl || $[122] !== handleRemoverDocumento || $[123] !== isClosed || $[124] !== isGuestWorkspace || $[125] !== isParteAtiva || $[126] !== loadingDocumentos || $[127] !== removingDocumentoId || $[128] !== tabEmExibicao || $[129] !== usarDocumentoPadrao) {
    t24 = tabEmExibicao === "documentos" && <ArbitralDocumentosTab documentos={documentos} isLoadingList={loadingDocumentos} isClosed={isClosed} podeRemoverDocumento={isParteAtiva} currentUserId={currentUserId} removingDocumentoId={removingDocumentoId} onDownload={handleDownloadDocumento} onDelete={handleRemoverDocumento} onGetPreviewUrl={handleGetPreviewUrl} podeBuscarDocumentoPadrao={!isGuestWorkspace} onUsarDocumentoPadrao={usarDocumentoPadrao} />;
    $[118] = currentUserId;
    $[119] = documentos;
    $[120] = handleDownloadDocumento;
    $[121] = handleGetPreviewUrl;
    $[122] = handleRemoverDocumento;
    $[123] = isClosed;
    $[124] = isGuestWorkspace;
    $[125] = isParteAtiva;
    $[126] = loadingDocumentos;
    $[127] = removingDocumentoId;
    $[128] = tabEmExibicao;
    $[129] = usarDocumentoPadrao;
    $[130] = t24;
  } else {
    t24 = $[130];
  }
  let t25;
  if ($[131] !== abrirComposerAndamento || $[132] !== currentUserName || $[133] !== errorAndamentos || $[134] !== handleCriarAndamento || $[135] !== handleGetPreviewUrl || $[136] !== handleRecarregarAndamentos || $[137] !== highlightAndamentoId || $[138] !== isClosed || $[139] !== loadingAndamentos || $[140] !== processo.andamentos || $[141] !== processo.numeroContrato || $[142] !== processo.partes || $[143] !== processo.tipoRito || $[144] !== processo.valorCausa || $[145] !== submittingAndamento || $[146] !== tabEmExibicao) {
    t25 = tabEmExibicao === "andamentos" && <ArbitralAndamentosTab andamentos={processo.andamentos} partes={processo.partes} usuarioNome={currentUserName} resumo={{
      valorCausa: processo.valorCausa,
      tipoRito: processo.tipoRito,
      numeroContrato: processo.numeroContrato
    }} isClosed={isClosed} isLoading={loadingAndamentos} error={errorAndamentos} onRecarregar={handleRecarregarAndamentos} isSubmitting={submittingAndamento} onCriarAndamento={handleCriarAndamento} onGetPreviewUrl={handleGetPreviewUrl} highlightAndamentoId={highlightAndamentoId} abrirComposerAoMontar={abrirComposerAndamento} onComposerAbertoAoMontar={() => setAbrirComposerAndamento(false)} />;
    $[131] = abrirComposerAndamento;
    $[132] = currentUserName;
    $[133] = errorAndamentos;
    $[134] = handleCriarAndamento;
    $[135] = handleGetPreviewUrl;
    $[136] = handleRecarregarAndamentos;
    $[137] = highlightAndamentoId;
    $[138] = isClosed;
    $[139] = loadingAndamentos;
    $[140] = processo.andamentos;
    $[141] = processo.numeroContrato;
    $[142] = processo.partes;
    $[143] = processo.tipoRito;
    $[144] = processo.valorCausa;
    $[145] = submittingAndamento;
    $[146] = tabEmExibicao;
    $[147] = t25;
  } else {
    t25 = $[147];
  }
  let t26;
  if ($[148] !== chatMessage || $[149] !== handleSendMessage || $[150] !== isClosed || $[151] !== isSendingMessage || $[152] !== mensagensChat || $[153] !== processo.partes || $[154] !== setChatMessage || $[155] !== tabEmExibicao || $[156] !== typingUsers) {
    t26 = tabEmExibicao === "chat" && <ArbitralChatTab mensagens={mensagensChat} partes={processo.partes} chatMessage={chatMessage} setChatMessage={setChatMessage} isSendingMessage={isSendingMessage} isClosed={isClosed} onSendMessage={handleSendMessage} typingUsers={typingUsers} />;
    $[148] = chatMessage;
    $[149] = handleSendMessage;
    $[150] = isClosed;
    $[151] = isSendingMessage;
    $[152] = mensagensChat;
    $[153] = processo.partes;
    $[154] = setChatMessage;
    $[155] = tabEmExibicao;
    $[156] = typingUsers;
    $[157] = t26;
  } else {
    t26 = $[157];
  }
  let t27;
  if ($[158] !== carregandoFinanceiro || $[159] !== cobrancas || $[160] !== erroFinanceiro || $[161] !== extratoApi || $[162] !== linkSuspenso || $[163] !== processoId || $[164] !== rawFinanceiro || $[165] !== recarregarFinanceiro || $[166] !== regerandoCobrancaId || $[167] !== regerarPorId || $[168] !== tabEmExibicao) {
    t27 = tabEmExibicao === "financeiro" && <ArbitralFinanceiroTab processoId={processoId} financeiro={rawFinanceiro} cobrancas={cobrancas} extrato={extratoApi} carregando={carregandoFinanceiro} erro={erroFinanceiro} onTentarNovamente={recarregarFinanceiro} regerandoId={regerandoCobrancaId} linkSuspenso={linkSuspenso} onRegerarCobranca={regerarPorId} />;
    $[158] = carregandoFinanceiro;
    $[159] = cobrancas;
    $[160] = erroFinanceiro;
    $[161] = extratoApi;
    $[162] = linkSuspenso;
    $[163] = processoId;
    $[164] = rawFinanceiro;
    $[165] = recarregarFinanceiro;
    $[166] = regerandoCobrancaId;
    $[167] = regerarPorId;
    $[168] = tabEmExibicao;
    $[169] = t27;
  } else {
    t27 = $[169];
  }
  let t28;
  if ($[170] !== envioFisicoAr.acompanhamentos || $[171] !== envioFisicoAr.carregado || $[172] !== envioFisicoAr.carregando || $[173] !== envioFisicoAr.carregandoMais || $[174] !== envioFisicoAr.carregarMais || $[175] !== envioFisicoAr.documentosAbrindoDe || $[176] !== envioFisicoAr.erro || $[177] !== envioFisicoAr.hasMore || $[178] !== envioFisicoAr.recarregar || $[179] !== envioFisicoAr.verDocumento || $[180] !== tabEmExibicao) {
    t28 = tabEmExibicao === "envio-fisico" && <EnvioFisicoArTab cartas={envioFisicoAr.acompanhamentos} carregando={envioFisicoAr.carregando || !envioFisicoAr.carregado} carregandoMais={envioFisicoAr.carregandoMais} hasMore={envioFisicoAr.hasMore} erro={envioFisicoAr.erro} tentandoDeNovo={envioFisicoAr.carregando || envioFisicoAr.carregandoMais} documentosAbrindoDe={envioFisicoAr.documentosAbrindoDe} onCarregarMais={envioFisicoAr.carregarMais} onTentarNovamente={envioFisicoAr.recarregar} onVerDocumento={envioFisicoAr.verDocumento} />;
    $[170] = envioFisicoAr.acompanhamentos;
    $[171] = envioFisicoAr.carregado;
    $[172] = envioFisicoAr.carregando;
    $[173] = envioFisicoAr.carregandoMais;
    $[174] = envioFisicoAr.carregarMais;
    $[175] = envioFisicoAr.documentosAbrindoDe;
    $[176] = envioFisicoAr.erro;
    $[177] = envioFisicoAr.hasMore;
    $[178] = envioFisicoAr.recarregar;
    $[179] = envioFisicoAr.verDocumento;
    $[180] = tabEmExibicao;
    $[181] = t28;
  } else {
    t28 = $[181];
  }
  let t29;
  if ($[182] !== certificados || $[183] !== certificadosLoading || $[184] !== handleBaixarCertificado || $[185] !== handleVerCertificadoAcessos || $[186] !== handleVerCertificadoAcessosSentenca || $[187] !== processo.status || $[188] !== tabEmExibicao) {
    t29 = tabEmExibicao === "certificados" && <ArbitralCertificadosTab certificados={certificados} isLoading={certificadosLoading} onVerCertificadoAcessos={handleVerCertificadoAcessos} onVerCertificadoAcessosSentenca={sentencaFoiProferida(processo.status) ? handleVerCertificadoAcessosSentenca : undefined} onBaixarCertificado={handleBaixarCertificado} />;
    $[182] = certificados;
    $[183] = certificadosLoading;
    $[184] = handleBaixarCertificado;
    $[185] = handleVerCertificadoAcessos;
    $[186] = handleVerCertificadoAcessosSentenca;
    $[187] = processo.status;
    $[188] = tabEmExibicao;
    $[189] = t29;
  } else {
    t29 = $[189];
  }
  let t30;
  if ($[190] !== t22 || $[191] !== t23 || $[192] !== t24 || $[193] !== t25 || $[194] !== t26 || $[195] !== t27 || $[196] !== t28 || $[197] !== t29) {
    t30 = <div>{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}</div>;
    $[190] = t22;
    $[191] = t23;
    $[192] = t24;
    $[193] = t25;
    $[194] = t26;
    $[195] = t27;
    $[196] = t28;
    $[197] = t29;
    $[198] = t30;
  } else {
    t30 = $[198];
  }
  let t31;
  if ($[199] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setIsCancelModalOpen(false);
    $[199] = t31;
  } else {
    t31 = $[199];
  }
  let t32;
  if ($[200] !== handleCancelProcess) {
    t32 = async (motivo_0, comentario) => {
      await handleCancelProcess(motivo_0, comentario);
      setIsCancelModalOpen(false);
    };
    $[200] = handleCancelProcess;
    $[201] = t32;
  } else {
    t32 = $[201];
  }
  let t33;
  if ($[202] !== isCancelModalOpen || $[203] !== isCanceling || $[204] !== podeCancelarDireto || $[205] !== t32) {
    t33 = <CancelProcessModal isOpen={isCancelModalOpen} onClose={t31} modoDireto={podeCancelarDireto} onConfirm={t32} isSubmitting={isCanceling} />;
    $[202] = isCancelModalOpen;
    $[203] = isCanceling;
    $[204] = podeCancelarDireto;
    $[205] = t32;
    $[206] = t33;
  } else {
    t33 = $[206];
  }
  let t34;
  if ($[207] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setIsReabrirModalOpen(false);
    $[207] = t34;
  } else {
    t34 = $[207];
  }
  let t35;
  if ($[208] !== handleReabrirProcesso) {
    t35 = async (novoStatus, motivo_1) => {
      await handleReabrirProcesso(novoStatus, motivo_1);
      setIsReabrirModalOpen(false);
    };
    $[208] = handleReabrirProcesso;
    $[209] = t35;
  } else {
    t35 = $[209];
  }
  let t36;
  if ($[210] !== isReabrirModalOpen || $[211] !== reabrindo || $[212] !== t35) {
    t36 = <ReabrirProcessoModal isOpen={isReabrirModalOpen} onClose={t34} onConfirm={t35} isSubmitting={reabrindo} />;
    $[210] = isReabrirModalOpen;
    $[211] = reabrindo;
    $[212] = t35;
    $[213] = t36;
  } else {
    t36 = $[213];
  }
  let t37;
  if ($[214] !== t16 || $[215] !== t17 || $[216] !== t18 || $[217] !== t19 || $[218] !== t20 || $[219] !== t21 || $[220] !== t30 || $[221] !== t33 || $[222] !== t36) {
    t37 = <div className="flex w-full flex-col gap-6 px-8 py-8">{t16}{t17}{t18}{t19}{t20}{t21}{t30}{t33}{t36}</div>;
    $[214] = t16;
    $[215] = t17;
    $[216] = t18;
    $[217] = t19;
    $[218] = t20;
    $[219] = t21;
    $[220] = t30;
    $[221] = t33;
    $[222] = t36;
    $[223] = t37;
  } else {
    t37 = $[223];
  }
  return t37;
}
function _temp4(p_0) {
  return !pendenciaEncerrada(p_0.status);
}
function _temp3(p) {
  return p.destinatario === "REQUERENTE" || p.destinatario === null;
}
function _temp2(s_0) {
  return s_0.tipo === "ALTERACAO_DADOS";
}
function _temp(s) {
  return s.tipo === "CANCELAMENTO";
}
export default ArbitralDetailPageContainer;